import { config } from '../config';
import { isValidLatLng, randomPointNear } from '../game/geo';
import { pickPlace, type GameMap } from '../game/maps';
import { GoogleMapsLoadError, hasGoogleAuthFailed, importStreetView } from './googleMaps';
import { batchSearch, isFarEnough } from './search';
import {
  NoPanoramaFoundError,
  ProviderUnavailableError,
  type FindOptions,
  type Panorama,
  type PanoramaProvider,
} from './types';

/**
 * Google Street View, through the official Maps JavaScript API (StreetViewService).
 * Needs a browser key with the "Maps JavaScript API" enabled (VITE_GOOGLE_MAPS_API_KEY).
 * Only official, outdoor Google imagery is used (no user photospheres).
 */

/** Random offset around the selected town before searching the nearest panorama. */
const JITTER_KM = 45;
/** Search radius of StreetViewService.getPanorama, in metres. */
const SEARCH_RADIUS_M = 50000;

class AuthFailure extends Error {}

export interface GooglePanoramaMeta {
  /** Initial heading: along the road when links are known. */
  initialHeading: number;
}

export function createGoogleProvider(apiKey: string = config.googleMapsApiKey): PanoramaProvider {
  return {
    id: 'google',
    name: 'Google Street View',
    description: 'La couverture la plus complète au monde. Nécessite une clé API Google Maps (offre gratuite mensuelle).',
    viewer: 'google',
    mapKind: 'google',
    envVar: 'VITE_GOOGLE_MAPS_API_KEY',
    isConfigured: () => apiKey.length > 0 && !hasGoogleAuthFailed(),
    supportedMaps: ['world', 'europe', 'france', 'americas', 'asia-oceania', 'africa'],

    async findRandom(map: GameMap, { signal, exclude, rng = Math.random }: FindOptions): Promise<Panorama> {
      if (!apiKey) throw new ProviderUnavailableError('google', 'Aucune clé Google Maps configurée (VITE_GOOGLE_MAPS_API_KEY).');
      let lib: google.maps.StreetViewLibrary;
      try {
        lib = await importStreetView(apiKey);
      } catch (error) {
        const message = error instanceof GoogleMapsLoadError ? error.message : 'Google Maps n’a pas pu être chargé.';
        throw new ProviderUnavailableError('google', message, error);
      }
      const service = new lib.StreetViewService();
      const minDistanceKm = map.scaleKm / 15;

      const result = await batchSearch({
        signal,
        batches: 5,
        parallel: 3,
        attempt: async (attemptSignal) => {
          if (hasGoogleAuthFailed()) throw new AuthFailure('auth');
          const place = pickPlace(map, 'google', rng);
          const location = randomPointNear(place, JITTER_KM, rng);
          let data: google.maps.StreetViewPanoramaData;
          try {
            ({ data } = await service.getPanorama({
              location,
              radius: SEARCH_RADIUS_M,
              preference: lib.StreetViewPreference.NEAREST,
              sources: [lib.StreetViewSource.GOOGLE, lib.StreetViewSource.OUTDOOR],
            }));
          } catch (error) {
            const code = (error as { code?: string })?.code;
            if (code === 'ZERO_RESULTS') return null;
            if (code === 'REQUEST_DENIED' || hasGoogleAuthFailed()) throw new AuthFailure(String(code));
            throw error;
          }
          if (attemptSignal.aborted) return null;
          const latLng = data.location?.latLng;
          const pano = data.location?.pano;
          if (!latLng || !pano) return null;
          const position = { lat: latLng.lat(), lng: latLng.lng() };
          if (!isValidLatLng(position) || !isFarEnough(position, exclude, minDistanceKm)) return null;
          // Official imagery is credited to Google; skip anything else that slipped through.
          if (data.copyright && !/google/i.test(data.copyright)) return null;
          const links = data.links ?? [];
          const initialHeading = links.length > 0 ? Number(links[Math.floor(rng() * links.length)]?.heading) || 0 : rng() * 360;
          return {
            provider: 'google',
            id: pano,
            position,
            heading: 0,
            imageUrls: [],
            attribution: {
              sourceName: 'Google Street View',
              author: data.copyright || undefined,
              capturedAt: data.imageDate || undefined,
              sourceUrl: `https://www.google.com/maps/@?api=1&map_action=pano&pano=${encodeURIComponent(pano)}`,
            },
            meta: { initialHeading } satisfies GooglePanoramaMeta,
          } satisfies Panorama;
        },
      });

      if (result.panorama) return result.panorama;
      if (result.errors.some((e) => e instanceof AuthFailure) || hasGoogleAuthFailed()) {
        throw new ProviderUnavailableError(
          'google',
          'La clé Google Maps a été refusée (clé invalide, domaine non autorisé ou facturation désactivée).',
        );
      }
      if (result.errors.length >= result.attempts) {
        throw new ProviderUnavailableError('google', 'Google Street View ne répond pas.', result.errors[0]);
      }
      throw new NoPanoramaFoundError('google', 'Aucun panorama Street View trouvé dans les zones explorées.');
    },
  };
}
